import type { LucideIcon } from 'lucide-react'
import type { ReactNode } from 'react'

import { cn } from '../lib/cn'

/**
 * Empty states here are documentation, not decoration (spec §9).
 *
 * "Aucune donnée" tells an installer nothing. The description has to name the
 * next concrete step — pair an IMEI, check the CAN program number, check the
 * server address — because the person looking at an empty fleet console is
 * usually the person who can fix it.
 */
export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  className,
  compact = false,
}: {
  icon?: LucideIcon
  title: string
  description?: string
  action?: ReactNode
  className?: string
  compact?: boolean
}) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center rounded-lg border border-dashed',
        'border-border bg-surface/60 text-center',
        compact ? 'gap-2 px-4 py-6' : 'gap-3 px-6 py-12',
        className,
      )}
    >
      {Icon ? (
        <div className="rounded-full bg-surface-muted p-2.5">
          <Icon aria-hidden className="h-5 w-5 text-foreground-subtle" />
        </div>
      ) : null}

      <div className="max-w-prose space-y-1.5">
        <p className="text-sm font-semibold text-foreground">{title}</p>
        {description ? (
          <p className="text-sm leading-relaxed text-foreground-muted">{description}</p>
        ) : null}
      </div>

      {action ? <div className="pt-1">{action}</div> : null}
    </div>
  )
}
